/**
 * LazyCollabEditor - mounts a collaborative editor immediately with a local
 * draft entity. The entity is only persisted server-side when the user types
 * actual content. This avoids creating empty entities and removes the
 * click-wait-type latency.
 *
 * Usage: mount this when no existing note is found (after children have loaded).
 * For existing persisted entities, use the standard DynamicEntityEditor.
 */

import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { Y } from '@tunasong/sync-lib'
import { fromUint8Array } from 'js-base64'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { CollabWebsocketEditorYDocProps } from './collab-editor-websocket-ydoc.js'
import { CollabWebsocketEditorYDoc } from './collab-editor-websocket-ydoc.js'

/** Recursively extract text from Slate nodes */
const extractText = (nodes: unknown[]): string =>
  nodes
    .map(node => {
      const n = node as Record<string, unknown>
      if ('text' in n) {
        return n.text as string
      }
      if ('children' in n && Array.isArray(n.children)) {
        return extractText(n.children as unknown[])
      }
      return ''
    })
    .join('')

export interface LazyCollabEditorProps extends Omit<CollabWebsocketEditorYDocProps, 'element' | 'plugins'> {
  plugins?: CollabWebsocketEditorYDocProps['plugins']
  /** Parent entity (for createEntity) */
  parent: Persisted<Entity>
  /** Draft entity template — must include at least `type`. The component adds id, yDoc, timestamps. */
  draft?: Partial<Entity>
  /** Create as private entity */
  isPrivate?: boolean
  /** Called after the entity has been persisted */
  onPersisted?: (entity: Persisted<Entity>) => void
}

export const LazyCollabEditor: FC<LazyCollabEditorProps> = props => {
  const {
    parent,
    draft,
    isPrivate = true,
    onPersisted,
    onEditor: onEditorProp,
    plugins: pluginsProp,
    ...restProps
  } = props
  const plugins = pluginsProp ?? []

  const { createEntity, createEntityStatus } = graphHooks.useEntityCreate<Entity>()
  const isPersisting = useRef(false)
  const [persisted, setPersisted] = useState(false)

  // If the API call fails in the background, allow retry
  useEffect(() => {
    if (createEntityStatus.isError) {
      isPersisting.current = false
      setPersisted(false)
    }
  }, [createEntityStatus.isError])

  const editorRef = useRef<TunaEditor | null>(null)
  const onChangePatched = useRef(false)

  // Persist the entity when the editor has non-empty content
  const persistIfNeeded = async () => {
    if (isPersisting.current || persisted || !editorRef.current) {
      return
    }

    const text = extractText(editorRef.current.children).trim()
    if (text.length === 0) {
      return
    }

    isPersisting.current = true

    // Encode the current YDoc state via the YjsEditor's sharedRoot
    const sharedRoot = (editorRef.current as unknown as { sharedRoot?: Y.XmlText }).sharedRoot
    const doc = sharedRoot?.doc
    const yDoc = doc ? fromUint8Array(Y.encodeStateAsUpdate(doc)) : (draft as { yDoc?: string }).yDoc

    const entity = { ...draft, yDoc } as Entity

    try {
      const result = await createEntity({ entity, parent, isPrivate })
      setPersisted(true)
      onPersisted?.(result)
    } catch {
      isPersisting.current = false
    }
  }

  const handleEditor = (editor: TunaEditor) => {
    editorRef.current = editor

    // Patch onChange only once to avoid stacking handlers if onEditor is called again
    if (!onChangePatched.current) {
      onChangePatched.current = true
      const { onChange } = editor
      editor.onChange = options => {
        onChange(options)
        void persistIfNeeded()
      }
    }

    onEditorProp?.(editor)
  }

  return <CollabWebsocketEditorYDoc {...restProps} plugins={plugins} element={draft as never} onEditor={handleEditor} />
}
